import React from 'react';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import DocsSiteSource from '@modules/eventStream/enum/DocsSiteSource';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import throwError from '@modules/utilities/error';
import RiskLevel from '../../types/RiskLevel';
import { getCurlCodeBlock, isSwaggerUITryItOutCancelButton } from './dom';
import { TryItOutEventParams } from './types';

/**
 * Determines if a warning dialog should be shown based on risk level and acceptance status
 */
export const shouldShowWarningDialog = (riskLevel: RiskLevel, isAccepted: boolean): boolean => {
  return riskLevel !== RiskLevel.LOW && !isAccepted;
};

/**
 * Logs Try It Out events with runtime validation for required parameters
 */
export const logTryItOutEvent = (
  eventType: DocsSiteEventType,
  params: TryItOutEventParams,
): void => {
  if (
    eventType === DocsSiteEventType.TryItOutClickTryItOut &&
    params.isTryItOutActive === undefined
  ) {
    throwError(
      `TryItOut: isTryItOutActive required for TryItOutClickTryItOut event ${params.method} ${params.url}`,
    );
    return;
  }

  if (eventType === DocsSiteEventType.TryItOutClickExecute && !params.authMode) {
    throwError(
      `TryItOut: authMode required for TryItOutClickExecute event ${params.method} ${params.url}`,
    );
    return;
  }

  if (eventType === DocsSiteEventType.TryItOutClickAcceptHighRiskWarning && !params.authMode) {
    throwError(
      `TryItOut: authMode required for TryItOutClickAcceptHighRiskWarning event ${params.method} ${params.url}`,
    );
    return;
  }

  if (eventType === DocsSiteEventType.TryItOutClickCancelHighRiskWarning && !params.authMode) {
    throwError(
      `TryItOut: authMode required for TryItOutClickCancelHighRiskWarning event ${params.method} ${params.url}`,
    );
    return;
  }

  unifiedLoggerClient.logClickEvent({
    eventName: eventType,
    parameters: {
      source: DocsSiteSource.TryItOut,
      method: params.method,
      url: params.url,
      riskLevel: params.riskLevel,
      ...(params.isTryItOutActive !== undefined && {
        isTryItOutActive: String(params.isTryItOutActive),
      }),
      ...(params.authMode && { authMode: String(params.authMode) }),
    },
  });
};

/**
 * Gets the appropriate text for try it out buttons based on try-it-out state
 */
export const getTryItOutButtonText = (
  isTryItOutActive: boolean,
  tryItOutText: string,
  closeTryItOutText: string,
): string => {
  return isTryItOutActive ? `✕\u00A0\u00A0${closeTryItOutText}` : tryItOutText;
};

/**
 * Creates a click handler for try it out buttons that handles warning dialogs and forwards clicks to underlying Swagger UI Try it out button
 */
export const createTryItOutButtonClickHandler = (
  riskLevel: RiskLevel,
  isWarningDialogAcceptedRef: React.MutableRefObject<boolean>,
  buttonElement: HTMLElement,
  swaggerTryItOutButton: Element,
  setLastClickedButton: (element: HTMLElement) => void,
  setShowWarningDialog: (show: boolean) => void,
  method: string,
  url: string,
): ((event: Event) => void) => {
  return (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    if (shouldShowWarningDialog(riskLevel, isWarningDialogAcceptedRef.current)) {
      setLastClickedButton(buttonElement);
      // Blur the button to remove focus before opening dialog (prevents aria-hidden violation)
      buttonElement.blur();
      setShowWarningDialog(true);
      return;
    }

    const isTryItOutActive = isSwaggerUITryItOutCancelButton(swaggerTryItOutButton);

    logTryItOutEvent(DocsSiteEventType.TryItOutClickTryItOut, {
      isTryItOutActive,
      method,
      url,
      riskLevel,
    });

    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window,
    });
    swaggerTryItOutButton.dispatchEvent(clickEvent);
  };
};

/**
 * Creates a click handler for cURL copy buttons that finds and copies cURL content to clipboard
 */
export const createCurlCopyButtonClickHandler = (
  tooltipContainer: HTMLDivElement,
  method: string,
  url: string,
  riskLevel: RiskLevel,
): (() => void) => {
  return () => {
    // Find the cURL text and copy it (search from the container's parent since we replaced the button)
    const curlCodeBlock = getCurlCodeBlock(tooltipContainer.parentElement);
    if (curlCodeBlock?.textContent) {
      navigator.clipboard?.writeText(curlCodeBlock.textContent);

      logTryItOutEvent(DocsSiteEventType.TryItOutClickCopyCurl, {
        method,
        url,
        riskLevel,
      });
    }
  };
};
